// React
import React, { useState, useContext } from "react";

// Utility functions - API endpoints - hooks
import { useTranslation } from "react-i18next";
import { rejectApplication } from "../../../../api/endpoints/application";
import { useKanbanDispatch, useKanbanState } from "../../../context/KanbanContext";

// Styled components
import { RejectAllButton } from "./RejectAll.styled";

// Components
import { PrimaryButton } from "../../../../components/Button";
import { Icon } from "../../../../components";
import { Modal } from "../../../../components";
import { Typography } from "../../../../components/Typography";
import { Grid } from "../../../../components/Grid";
import EditRejectionEmail from "../../../components/EditRejectionEmail/EditRejectionEmail";
import { DispatchErrorContext } from "../../../../context/ErrorContext";
import { ShimmerText } from "../../../components/Shimmer/Shimmer";

const RejectAll = ({ positionId }) => {
  const { t } = useTranslation();
  const errorDispatch = useContext(DispatchErrorContext);
  const [showModal, setShowModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const { compactApplications } = useKanbanState();
  const { updateCompactApplication } = useKanbanDispatch();
  const applicationsToReject = compactApplications.to_be_rejected;

  const handleClose = () => {
    setOpen(false);
  };

  async function handleRejectApplications(applications) {
    try {
      setShowModal(false);

      setWaitingForConfirmation(true);

      const response = await rejectApplication({
        params: {
          applicationIds: applications.map((app) => app.id),
        },
      });

      if (!response?.success) {
        setWaitingForConfirmation(false);
        errorDispatch({ type: "set_error", payload: response?.message || "An error occurred while rejecting applications." });
        return;
      }

      await Promise.all(
        applications.map((application) =>
          updateCompactApplication({
            applicationId: application.id,
            substatus: "rejected",
            persist: false,
          }),
        ),
      );

      setConfirmation(true);

      setWaitingForConfirmation(false);
    } catch (error) {
      // Handle the error
      setShowModal(true);
      setWaitingForConfirmation(false);
      errorDispatch({ type: "set_error", payload: error.message });
      console.error("An error occurred while rejecting applications:", error);
    }
  }

  return (
    <>
      <RejectAllButton>
        <PrimaryButton transparent noPadding onClick={() => setShowModal(true)}>
          {t("common.rejectAll")}
          <Icon icon="edit_user" mr="8" />
        </PrimaryButton>
      </RejectAllButton>
      {showModal && (
        <>
          <Modal title={t("common.rejectAllModalTitle")} onClose={() => setShowModal(false)}>
            <Grid padding={25} fluid alignCenter>
              {applicationsToReject.length !== 0 ? (
                <>
                  <Typography tag="p" nomargin mb="20">
                    {t("common.confirmRejectAll")}
                  </Typography>
                  <PrimaryButton white onClick={() => setOpen(true)} mb="25">
                    {t("component.metaeditor.edit_rejection_email", "Edit rejection email")}
                    <Icon icon="edit" ml="0" mr="8" scale={1.2} lowOpacity />
                  </PrimaryButton>
                  <PrimaryButton red mt="0" onClick={() => handleRejectApplications(applicationsToReject)}>
                    {t("common.yesValidation")}
                    <Icon icon="checkmark" mr="12" scale="2" />
                  </PrimaryButton>
                </>
              ) : (
                <Typography tag="p" nomargin>
                  {t("common.noCandidatesToBeRejected")}
                </Typography>
              )}
              <PrimaryButton transparent mt="20" onClick={() => setShowModal(false)}>
                {t("common.cancel")}
                <Icon icon="cross" mr="12" scale="1" />
              </PrimaryButton>
            </Grid>
          </Modal>
        </>
      )}
      {(waitingForConfirmation || confirmation) && (
        <>
          <Modal title={confirmation ? t("common.rejectedCandidates") : t("common.rejectingCandidates")} onClose={() => setConfirmation(false)} noClose={confirmation ? false : true}>
            <Grid padding={25} fluid alignCenter>
              {confirmation ? (
                <>
                  <Typography tag="p" nomargin mb="20">
                    {t("common.rejectedCandidatesDesc")}
                  </Typography>
                  <PrimaryButton red mt="20" onClick={() => setConfirmation(false)}>
                    {t("common.great")}
                    <Icon icon="thumbs_up" mr="8" scale="1" />
                  </PrimaryButton>
                </>
              ) : (
                <>
                  <ShimmerText words={8} mbLG="10" tag="p" small nomargin mb="10" fontSize="14" />
                  <ShimmerText words={1} tag="p" small nomargin mt="20" fontSize="50" />
                </>
              )}
            </Grid>
          </Modal>
        </>
      )}
      <EditRejectionEmail positionId={positionId} ml="16" mb="25" open={open} onClose={handleClose} />
    </>
  );
};

export default RejectAll;
